import { Mediaclip } from 'app/interfaces/mediaclip';
import { ProjectStatsConfiguration } from './../../../interfaces/project';
import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { BbCanvasElementComponent } from './../../shared/bb-canvas-element/bb-canvas-element.component';
import { ChartArea } from 'chart.js';
import { Params, ActivatedRoute, Router } from '@angular/router';
import { Subscription, asyncScheduler, fromEvent, Observable } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { DatePresetsSelectorService } from './../../../services/date-presets-selector.service';
import { DateRange } from './../../../interfaces/dateRange';
import {
	AnalyticsService, AnalyticsRangeDataset, AnalyticsGranularity, StatsDateRange, AnalyticsStatusSet, AnalyticsFacetItem
} from './../../../services/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { BbSelectOption } from 'app/components/shared/bb-select/bb-select.component';
import { AfterContentInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { HamburgerItem, HamburgerOptionSelected } from 'app/components/shared/bb-hamburger-overlay/bb-hamburger-overlay.component';
import { DeviceInfo, FeatureDetectionService } from 'app/services/feature-detection.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const HOUR_IN_MILLISECONDS = 3600000;
@Component({
	templateUrl: './analytics-dashboard.component.html',
	styleUrls: ['./analytics-dashboard.component.scss']
})

export class AnalyticsDashboardComponent implements OnInit, AfterContentInit, OnDestroy {
	@ViewChild('geochartContainer') geoChartContainer: ElementRef;
	@ViewChild(BbCanvasElementComponent) canvasElementComponent: BbCanvasElementComponent;

	public datePresets: BbSelectOption[] = [
		{ value: 'custom', label: this.translate.instant('CUSTOM') },
		{ value: 'today', label: this.translate.instant('TODAY') },
		{ value: 'yesterday', label: this.translate.instant('YESTERDAY') },
		// {value: 'last-24-hours', label: this.translate.instant('LAST_X_HOURS', { x: '24' }) },
		// {value: 'last-48-hours', label: this.translate.instant('LAST_X_HOURS', { x: '48' }) },
		// {value: 'last-72-hours', label: this.translate.instant('LAST_X_HOURS', { x: '72' }) },
		{ value: 'last-7-days', label: this.translate.instant('LAST_X_DAYS', { x: '7' }) },
		{ value: 'last-30-days', label: this.translate.instant('LAST_X_DAYS', { x: '30' }) },
		{ value: 'last-6-months', label: this.translate.instant('LAST_6_MONTHS') },
		{ value: 'week-to-date', label: this.translate.instant('WEEK_TO_DATE') },
		{ value: 'last-week', label: this.translate.instant('LAST_WEEK') },
		{ value: 'month-to-date', label: this.translate.instant('MONTH_TO_DATE') },
		{ value: 'last-month', label: this.translate.instant('LAST_MONTH') },
		{ value: 'quarter-to-date', label: this.translate.instant('QUARTER_TO_DATE') },
		{ value: 'last-quarter', label: this.translate.instant('LAST_QUARTER') },
		{ value: 'year-to-date', label: this.translate.instant('YEAR_TO_DATE') },
		{ value: 'last-year', label: this.translate.instant('LAST_YEAR') },
	];

	viewRangeGraphDatasets: any;
	views: number;
	range: number; // milliseconds
	defaultFacetCount = 10;
	expandedFacets: any = {};
	theme: 'analytics' | 'analytics-mobile' = 'analytics';
	isTouchDevice = false;
	statsMinDate = new Date('1 jan 2019');

	mainGraphLoading = false;
	mainGraphNoData = false;
	mainGraphRangeDataset: any;
	facetRangeDatasets: any;
	mainGraphMenuOptions: HamburgerItem[] = [
		{
			headerLabel: this.translate.instant('GRANULARITY'),
			headerValue: 'granularity',
			headerIcon: 'fa-clock-o',
			options: [
				{ label: this.translate.instant('AUTOMATIC'), value: 'auto', active: true, disabled: false },
				{ label: this.translate.instant('MINUTES'), value: 'minute', active: false, disabled: false },
				{ label: this.translate.instant('HOURS'), value: 'hour', active: false, disabled: false },
				{ label: this.translate.instant('DAYS'), value: 'day', active: false, disabled: false },
				{ label: this.translate.instant('WEEKS'), value: 'week', active: false, disabled: false },
				{ label: this.translate.instant('MONTHS'), value: 'month', active: false, disabled: false },
				{ label: this.translate.instant('YEARS'), value: 'year', active: false, disabled: false }
			]
		}
	];

	statsProgressConfiguration: ProjectStatsConfiguration = {};
	mapInitialized = false;
	forceResizeMap = true; // first time also force resize to have correct initial size
	mapChart: any;
	mapRegion: 'world' | '002' | '019' | '142' | '150' | '009' = 'world';
	countryLookup: { [key: string]: string };
	mapMenuOptions: HamburgerItem[] = [
		{
			headerLabel: this.translate.instant('REGION'),
			headerValue: 'region',
			headerIcon: 'fa-globe',
			options: [
				// https://developers.google.com/chart/interactive/docs/gallery/geochart#continent-hierarchy-and-codes
				{ label: this.translate.instant('AFRICA'), value: '002', active: false },
				{ label: this.translate.instant('AMERICAS'), value: '019', active: false },
				{ label: this.translate.instant('ASIA'), value: '142', active: false },
				{ label: this.translate.instant('EUROPE'), value: '150', active: false },
				{ label: this.translate.instant('OCEANIA'), value: '009', active: false },
				{ label: this.translate.instant('WORLD'), value: 'world', active: true }
			]
		}
	];
	windowResize: Observable<Event>;
	facetGroups: any[];
	topClipsData: AnalyticsFacetItem[];
	activeId: string;
	contentSearchOpen = false;
	playerAPI: BBPlayerAPI;
	chartArea: ChartArea;
	timeSliderCanvasContext: CanvasRenderingContext2D;
	currentTime = 0;
	playerClipLoaded = false;
	numberBlocks: string[] = ['views', 'playerLoads', 'playRate', 'uniqueViewers', 'viewsPerUniqueViewer'];
	activeFilterList: any[];
	facetSelectorOptions: any[];

	private subscriptions = new Subscription();

	constructor(
		private translate: TranslateService,
		private router: Router,
		public datePresetsSelectorService: DatePresetsSelectorService,
		public analyticsService: AnalyticsService,
		private renderer: Renderer2,
		private activatedRoute: ActivatedRoute,
		private elRef: ElementRef,
		private featureDetectionService: FeatureDetectionService
	) {
		this.countryLookup = require('../../../resources/defaults/countryCodes.json');
	}

	ngOnInit() {
		this.doSubscriptions();
		this.determineTheme();
		this.initializeMap();
		this.subscriptions.add(this.analyticsService.filterConfigChange.subscribe(() => {
			this.facetGroups = this.analyticsService.getFacetGroups();
			this.facetSelectorOptions = this.analyticsService.filterConfigs.map((filterConfig) => {
				const ret = { ...filterConfig } as any;
				ret.value = ret.name;
				ret.label = `${ret.category}: ${ret.label}`;
				return ret;
			}).filter((option) => !this.analyticsService.status.activeFacets[option.value]);
		}));
		this.initializeWindowResizeListener();
	}

	ngAfterContentInit() {
		this.triggerWindowResize();
	}


	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onFilterSelected(filter) {
		this.toggleFacet(filter);
		this.scrollToFilter(filter);
		this.analyticsService.status.openCat[filter.category] = true;
	}

	scrollToFilter(filter) {
		const comp = this.elRef.nativeElement as HTMLElement;
		setTimeout(() => {
			const facetEl = comp.querySelector(`.facet-${filter.name}`);
			if (!this.isInViewport(facetEl, 150)) {
				facetEl.scrollIntoView({
					behavior: 'smooth'
				});
			}
		});
	}

	isInViewport(element, verticalInviewMargin: number = 0) {
		if (!element) {
			return false;
		}
		const rect = element.getBoundingClientRect();
		return (
			rect.top >= verticalInviewMargin &&
			rect.left >= 0 &&
			rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) - verticalInviewMargin &&
			rect.right <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	onIncludeSelected(facet) {
		this.analyticsService.status.facetconfig[facet.name].not = false;

		if (this.analyticsService.status.filters[facet.name]) {
			this.analyticsService.update();
		}
	}

	onExcludeSelected(facet) {
		this.analyticsService.status.facetconfig[facet.name].not = true;
		if (this.analyticsService.status.filters[facet.name]) {
			this.analyticsService.update();
		}
	}

	onSelectedClipChange() {
		if (this.activeId) {
			this.router.navigate(['/analytics/statistics/mediaclip/', this.activeId]);
		} else {
			this.router.navigate(['/analytics/statistics']);
		}
	}

	onFilterChange() {
		this.mainGraphLoading = true;
	}

	onFacetHighlightChange() {
		this.mainGraphLoading = true;
		// TODO: implement facet highlights and remove timeout below
		setTimeout(() => {
			this.mainGraphLoading = false;
		}, 1500);
	}

	updateActiveFilters(filters) {
		this.activeFilterList = Object.keys(filters)
			.filter(facetName => !(facetName === 'eid' && this.activeId))
			.map((facetName) => ({
				name: facetName,
				values: filters[facetName]
			}));
	}

	doSubscriptions() {
		this.subscriptions.add(this.featureDetectionService.deviceInfoObservable.subscribe((info: DeviceInfo) => {
			this.isTouchDevice = info.isTouchDevice;
			this.determineTheme();
		}));
		this.subscriptions.add(this.analyticsService.rangeDataSubject.subscribe((rangeDataset: AnalyticsRangeDataset) => {
			this.onRangeDatasetChange(rangeDataset);
		}));
		this.subscriptions.add(this.analyticsService.facetRangeDataSubject.subscribe((facetRangeData: AnalyticsRangeDataset) => {
			this.onFacetRangeDatasetChange(facetRangeData);
		}));
		this.subscriptions.add(this.analyticsService.statusSubject.subscribe((status: AnalyticsStatusSet) => {
			if (status.granularity) {
				this.setActiveGranularity(status.granularity);
			}
			setTimeout(() => {
				this.updateActiveFilters(status.filters);
			});
		}));
		this.subscriptions.add(this.analyticsService.topClipsSubject.subscribe((data: AnalyticsFacetItem[]) => {
			this.onTopClipsChange(data);
		}));
		this.subscriptions.add(this.activatedRoute.params.subscribe((params: Params) => {
			this.playerClipLoaded = false;
			this.activeId = params.id;
			this.analyticsService.setRoute(params.type, params.id);
			if (this.activeId) {
				this.analyticsService.status.openCat.Content = true;
			}
		}));
	}

	onTopClipsChange(data) {
		this.topClipsData = data;
		setTimeout(() => {
			// container size might have changed: resize map (when besides each other)
			this.drawMap();
		});
	}

	determineTheme(): void {
		const customDateIndex = this.datePresets.findIndex(preset => preset.value === 'custom');
		if (window.innerWidth < 750 || this.isTouchDevice) {
			this.theme = 'analytics-mobile';
			if (customDateIndex >= 0) {
				this.datePresets.splice(customDateIndex, 1);
			}
		} else {
			this.theme = 'analytics';
			if (customDateIndex === -1) {
				this.datePresets.unshift({ value: 'custom', label: this.translate.instant('CUSTOM') });
			}
		}
	}

	setGranularity(granularity: AnalyticsGranularity) {
		this.mainGraphLoading = true;
		this.analyticsService.setGranularity(granularity);
	}

	setMainGraphDatasets() {
		this.viewRangeGraphDatasets = [];
		this.viewRangeGraphDatasets.push(this.mainGraphRangeDataset && this.mainGraphRangeDataset.chartDataset ? this.mainGraphRangeDataset.chartDataset : []);
		const facetNames = this.facetRangeDatasets && Object.keys(this.facetRangeDatasets);
		if (facetNames && facetNames.length) {
			facetNames.forEach((facetName) => {
				const facetSet = this.facetRangeDatasets[facetName];
				const facetValues = Object.keys(facetSet);
				if (facetValues && facetValues.length) {
					facetValues.forEach((facetValue) => {
						if (facetSet[facetValue] && facetSet[facetValue].chartDataset) {
							const color = this.analyticsService.highlightColorRef[facetName + ':' + facetValue];
							facetSet[facetValue].chartDataset.backgroundColor = facetSet[facetValue].chartDataset.borderColor = color;
							this.viewRangeGraphDatasets.push(facetSet[facetValue].chartDataset);
						}
					});
				}
			});
		}
	}

	onRangeDatasetChange(rangeDataset: AnalyticsRangeDataset) {
		this.mainGraphLoading = false;
		if (rangeDataset === null) {
			this.mainGraphNoData = true;
			this.views = null;
			this.forceResizeMap = true;
			return;
		}
		this.mainGraphRangeDataset = rangeDataset;
		this.mainGraphNoData = false;
		this.setMainGraphDatasets();
		this.views = rangeDataset && rangeDataset.total || 0;
		this.drawMap();
		this.setDisabledGranularity();
	}

	onFacetRangeDatasetChange(facetRangeDatasets: any) {
		this.facetRangeDatasets = facetRangeDatasets;
		this.setMainGraphDatasets();
	}

	onFacetTitleClick(facet) {
		this.toggleFacet(facet);
	}

	toggleFacet(facet) {
		const filtersArr = this.analyticsService.status.filters[facet.name];
		if (!filtersArr?.length) {
			this.analyticsService.setFacetCount(facet.name, this.expandedFacets[facet.name] ? 0 : this.defaultFacetCount);
		}
		this.expandedFacets[facet.name] = !this.expandedFacets[facet.name];
	}

	addGoogleChartScriptToHead(): HTMLScriptElement {
		const script = this.renderer.createElement('script');
		this.renderer.setAttribute(script, 'src', 'https://www.gstatic.com/charts/loader.js');
		this.renderer.appendChild(document.head, script);
		return script;
	}

	initializeMap(): void {
		if (!(window as any).googleMapsReady) {
			// edge case where index.html did not load maps.googleapis yet.
			setTimeout(() => {
				this.initializeMap();
			}, 1000);
			return;
		}

		if (!window.google?.charts) {
			this.addGoogleChartScriptToHead().onload = () => {
				this.initializeMap();
			};
			return;
		}

		window.google.charts.load('current', {
			packages: ['geochart'],
			mapsApiKey: 'ABQIAAAAa9WXCNoJi1-57MAIQtai4RSRfxaOOCXdPNQnADesIg5ou7_2QRQje4cr2MT9kv3rbbRs2WJHYgaqMw'
		});
		google.charts.setOnLoadCallback(() => {
			this.mapInitialized = true;
			this.drawMap();
		});
	}

	onWindowResize() {
		this.drawMap();
		this.determineTheme();
	}

	initializeWindowResizeListener(): void {
		this.windowResize = fromEvent(window, 'resize');
		this.subscriptions.add(
			this.windowResize.pipe(throttleTime(200, asyncScheduler, { trailing: true })).subscribe(() => {
				this.onWindowResize();
			})
		);
	}

	setMapFilter(data: HamburgerOptionSelected): void {
		switch (data.headerValue) {
			case 'region':
				this.mapMenuOptions[0].options.map(option => {
					option.active = option.value === data.value;
					return option;
				});
				this.mapRegion = data.value;
				this.drawMap();
				break;
		}
	}

	setMenuFilter(data: HamburgerOptionSelected): void {
		switch (data.headerValue) {
			case 'granularity':
				this.setGranularity(data.value);
				break;
		}
	}

	statsProgressMediaclipChanged(clip: Mediaclip) {
		// navigate to clip loaded in the player
		if (!clip?.id) {
			return;
		}
		this.router.navigate(['/analytics/statistics/mediaclip/', clip.id]);
	}

	onDateSelected(dateRange: DateRange) {
		this.range = (dateRange.endDate.getTime() - dateRange.startDate.getTime());
		this.onDateChange({
			fromDate: dateRange.startDate.toJSON(),
			toDate: dateRange.endDate.toJSON()
		});
	}

	onDateChange(dateRange: StatsDateRange) {
		this.mainGraphLoading = true;
		this.analyticsService.setDateRange(dateRange);
		this.statsProgressConfiguration = {
			statsDateStart: new Date(dateRange.fromDate),
			statsDateEnd: new Date(dateRange.toDate)
		};
	}

	/*
		DROPOFF CHART METHODS
	*/

	updateCanvasContext(context: CanvasRenderingContext2D) {
		this.timeSliderCanvasContext = context;
		if (this.currentTime !== undefined) {
			this.updateTimeSlider(this.currentTime);
		}
	}

	updateTimeSlider(currentTime: number) {
		const milliSeconds: number = currentTime * 1000;
		if (this.timeSliderCanvasContext && this.chartArea) {
			const chartWidth = this.chartArea.right - this.chartArea.left;
			const position = this.chartArea.left + ((chartWidth / this.analyticsService.mediaclip.length) * currentTime);
			if (this.canvasElementComponent) {
				this.canvasElementComponent.clearCanvas();
			}
			this.timeSliderCanvasContext.beginPath();
			this.timeSliderCanvasContext.globalAlpha = 0.4;
			this.timeSliderCanvasContext.strokeStyle = 'red';
			this.timeSliderCanvasContext.moveTo(position, 0);
			this.timeSliderCanvasContext.lineTo(position, 250);
			this.timeSliderCanvasContext.lineWidth = 1;
			this.timeSliderCanvasContext.stroke();
			this.timeSliderCanvasContext.font = '13px Lato';
			this.timeSliderCanvasContext.fillText(this.getTimeFormat(milliSeconds), position + 5, 18);
			this.timeSliderCanvasContext.fillStyle = '#858585';
			this.timeSliderCanvasContext.closePath();
		}
	}

	updateChartArea(chartArea: ChartArea) {
		this.chartArea = chartArea;
		this.updateTimeSlider(0);
	}

	getTimeFormat(milliSeconds: number): string {
		if (milliSeconds > 0) {
			let format = 'mm:ss';
			if (milliSeconds > HOUR_IN_MILLISECONDS) {
				format = 'HH:mm:ss';
			}
			// utc to avoid local timezone issues (which would add/subtract hours)
			return dayjs(milliSeconds).utc().format(format);
		}
		return '';
	}

	triggerWindowResize() {
		window.dispatchEvent(new Event('resize'));
	}

	progressIndicatorClick(event: MouseEvent) {
		if (event.offsetX >= this.chartArea.left && event.offsetX <= this.chartArea.right) {
			const canvasWidth = this.chartArea.right - this.chartArea.left;
			const seconds = (event.offsetX - this.chartArea.left) / canvasWidth * this.analyticsService.mediaclip.length;
			if (this.playerAPI) {
				if (this.playerAPI.getPhase() !== 'MAIN') {
					this.playerAPI.play();
				}
				this.playerAPI.seek(seconds);
			}
			this.updateTimeSlider(seconds);
		}
	}

	onPlayerCreated(player: any) {
		this.playerAPI = player;

		player.on('loadedclipdata', (ev: any) => {
			this.playerClipLoaded = true;
			setTimeout(() => {
				this.updateTimeSlider(0);
			}, 1000);
		});

		player.on('timeupdate seeked', (ev: any) => {
			this.currentTime = player.getCurrentTime();
			this.updateTimeSlider(this.currentTime);
		});
	}

	/*
		END: DROPOFF CHART METHODS
	*/

	private drawMap(): void {
		if (!this.mapInitialized || !this.analyticsService.facetData.country) {
			return;
		}

		const mapData: [string | { v: string; f: string }, string | number][] = [['Country', 'Views']];

		let filteredCountries = [];
		const countryFacetFilter = this.analyticsService?.facetconfigParam?.country;
		if (countryFacetFilter?.query) {
			filteredCountries = countryFacetFilter.query.map((countryName: string) => JSON.parse(countryName));
		}

		this.analyticsService.facetData.country.forEach((country: { count: number; value: string }) => {
			// exclude/include (.not boolean) countries in world map when set as facetConfig Filter
			if (filteredCountries.length > 0
				&& (countryFacetFilter.not ? filteredCountries.includes(country.value) : !filteredCountries.includes(country.value))
			) {
				return;
			}

			// value is the full country name - lookup country iso code
			const countryCode = country.value ? this.countryLookup[country.value] : null;
			if (countryCode) {
				mapData.push([{ v: countryCode, f: country.value }, country.count]);
			}
		});

		const data = google.visualization.arrayToDataTable(mapData);

		const options = {
			region: this.mapRegion,
			datalessRegionColor: '#f7f7f7',
			defaultColor: 'white',
			backgroundColor: 'white',
			colorAxis: { colors: ['#ebf3fa', '#3f536e'] } // blueish gray -> $analytics-dark
		};

		if (this.mapChart) {
			this.mapChart.clearChart();
		}

		const chart = new google.visualization.GeoChart(this.geoChartContainer.nativeElement);

		// country border color cannot be set as option or by css
		google.visualization.events.addListener(chart, 'ready', () => {
			const countries = this.geoChartContainer.nativeElement.getElementsByTagName('path');
			Array.prototype.forEach.call(countries, (path: HTMLElement) => {
				path.setAttribute('stroke', '#7ab1df');
				path.setAttribute('stroke-width', '0.25');
			});
		});

		chart.draw(data, options);
		this.mapChart = chart;

		if (this.forceResizeMap) {
			// bug Google chart?
			// when no results switches to X results. Manually draw again to keep map within container boundaries (width)
			this.forceResizeMap = false;
			setTimeout(() => {
				this.drawMap();
			});
		}
	}

	// when the date range changes: check which granularity options are to be disabled
	private setDisabledGranularity(): void {
		const rangeSeconds = this.range / 1000;
		this.mainGraphMenuOptions[0].options.map(option => {
			option.disabled = !this.analyticsService.getGranularityAllowed(option.value, rangeSeconds);
			return option;
		});
	}

	private setActiveGranularity(granularity: AnalyticsGranularity): void {
		this.mainGraphMenuOptions[0].options.map(option => {
			option.active = option.value === granularity;
			return option;
		});
	}
}
